/**
 * The pre-HOF-627 invite form (SMS invite link, employee/farmer quota), inside Settings → Team.
 *
 * Kept while legacy invitations exist: the Team list shows them read-only ("Alte Einladung") and
 * points here to manage them. Owner-only, on the owner's own farm — `SettingsPage` gates it.
 * The behaviour and the error mapping are the page's, moved verbatim.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InviteForm, InviteTable, SettingsRow, SettingsSection } from '@hofsync/design-system';
import { useToast } from '../../contexts/ToastContext';
import { inviteService, type Invite, type InviteLimits, type InviteType } from '../../services/inviteService';

const DEFAULT_LIMITS = { employeeRemaining: 0, employeeLimit: 5, farmerRemaining: 0, farmerResetDate: undefined };

export default function LegacyInvitesSection() {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [invites, setInvites] = useState<Invite[]>([]);
    const [limits, setLimits] = useState<InviteLimits | null>(null);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [revoking, setRevoking] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [invitesResponse, limitsResponse] = await Promise.all([inviteService.getInvites(), inviteService.getLimits()]);
            setInvites(invitesResponse.invites);
            setLimits(limitsResponse);
        } catch (loadError) {
            console.error('Failed to load invite data:', loadError);
            showToast(t('invites.loadError'), 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast, t]);

    useEffect(() => {
        load();
    }, [load]);

    const create = async (phone: string, type: InviteType) => {
        setCreating(true);
        setError(undefined);
        try {
            await inviteService.createInvite({ phone, invite_type: type });
            showToast(t('invites.createSuccess'), 'success');
            await load();
        } catch (createError: any) {
            console.error('Failed to create invite:', createError);
            const status = createError.response?.status;
            const detail = createError.response?.data?.detail;
            if (status === 400) {
                if (detail === 'Farmer invite limit reached') setError(t('invites.farmerLimitReached'));
                else if (detail === 'Employee invite limit reached') setError(t('invites.employeeLimitReached'));
                else if (detail === 'User already in system') setError(t('invites.userAlreadyExists'));
                else if (detail === 'Invite already sent') setError(t('invites.alreadyInvited'));
                else setError(detail || t('invites.createError'));
            } else if (status === 422) {
                if (Array.isArray(detail) && detail.length > 0) {
                    // Pydantic validation error format: [{loc: [...], msg: "...", type: "..."}]
                    const msg = detail[0]?.msg || '';
                    const humanReadable = msg.includes(': ') ? msg.split(': ').slice(1).join(': ') : msg;
                    if (msg.startsWith('PHONE_MISSING_COUNTRY_CODE')) setError(t('invites.phoneMissingCountryCode'));
                    else if (msg.startsWith('PHONE_UNSUPPORTED_COUNTRY')) setError(t('invites.phoneUnsupportedCountry'));
                    else if (msg.startsWith('PHONE_TOO_SHORT')) setError(t('invites.phoneTooShort'));
                    else if (msg.startsWith('PHONE_TOO_LONG')) setError(t('invites.phoneTooLong'));
                    else if (msg.startsWith('PHONE_INVALID_CHARACTERS')) setError(t('invites.phoneInvalidCharacters'));
                    else setError(humanReadable || t('invites.createError'));
                } else if (typeof detail === 'string') {
                    setError(detail);
                } else {
                    setError(t('invites.createError'));
                }
            } else {
                setError(t('invites.createError'));
            }
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (inviteId: string) => {
        setRevoking(true);
        try {
            await inviteService.revokeInvite(inviteId);
            showToast(t('invites.revokeSuccess'), 'success');
            await load();
        } catch (revokeError) {
            console.error('Failed to revoke invite:', revokeError);
            showToast(t('invites.revokeError'), 'error');
        } finally {
            setRevoking(false);
        }
    };

    return (
        <SettingsSection title={t('settings.invites.title')} description={t('settings.invites.subtitle')}>
            <SettingsRow label={t('settings.invites.sendInvite')} stacked>
                <InviteForm
                    limits={
                        limits
                            ? {
                                  employeeRemaining: limits.employee_remaining,
                                  employeeLimit: limits.employee_limit,
                                  farmerRemaining: limits.farmer_remaining,
                                  farmerResetDate: limits.farmer_reset_date || undefined,
                              }
                            : DEFAULT_LIMITS
                    }
                    onSubmit={create}
                    loading={creating || loading}
                    error={error}
                    labels={{
                        phonePlaceholder: t('settings.invites.phonePlaceholder'),
                        employeeLabel: t('settings.invites.typeEmployee'),
                        farmerLabel: t('settings.invites.typeFarmer'),
                        submitLabel: t('settings.invites.sendInvite'),
                        loadingLabel: t('settings.invites.sending'),
                    }}
                />
            </SettingsRow>
            <SettingsRow label={t('settings.invites.activeInvites')} stacked>
                <InviteTable
                    invites={invites.map((invite) => ({
                        id: invite.id,
                        phone: invite.phone,
                        type: invite.invite_type,
                        status: invite.status,
                        createdAt: invite.created_at,
                        expiresAt: invite.expires_at,
                    }))}
                    onRevoke={revoke}
                    revokeLoading={revoking}
                    emptyMessage={t('settings.invites.noInvites')}
                />
            </SettingsRow>
        </SettingsSection>
    );
}
